import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Plus, Minus, Trash2, CalendarDays } from "lucide-react";
import type { Product, Client } from "@shared/schema";
import { format, addDays, startOfDay } from "date-fns";

type CartItem = {
  productId: string;
  product: Product;
  quantity: number;
};

interface AddOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showStatusAndDate?: boolean;
}

const statusOptions = [
  { value: "pending", label: "Väntande" },
  { value: "confirmed", label: "Bekräftad" },
  { value: "in_production", label: "I produktion" },
  { value: "packed", label: "Färdig att leverera" },
  { value: "delivered", label: "Levererad" },
];

export default function AddOrderDialog({ open, onOpenChange, showStatusAndDate = false }: AddOrderDialogProps) {
  const { toast } = useToast();
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [notes, setNotes] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("confirmed");
  const [orderDate, setOrderDate] = useState<string>(new Date().toISOString().slice(0, 16));
  const [deliveryDate, setDeliveryDate] = useState<string>("");

  const minDeliveryDate = format(addDays(startOfDay(new Date()), 1), "yyyy-MM-dd");

  const { data: clients } = useQuery<Client[]>({
    queryKey: ["/api/admin/clients"],
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: ["/api/admin/products"],
  });

  const createOrderMutation = useMutation({
    mutationFn: (data: {
      clientId: string;
      items: { productId: string; quantity: number }[];
      status?: string;
      orderDate?: string;
      deliveryDate?: string;
      notes?: string;
    }) => apiRequest("POST", "/api/admin/orders", data),
    onSuccess: () => {
      toast({ title: "Order skapad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dashboard"] });
      onOpenChange(false);
      resetForm();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte skapa order.", variant: "destructive" });
    },
  });

  const resetForm = () => {
    setSelectedClientId("");
    setCart([]);
    setNotes("");
    setSelectedStatus("confirmed");
    setOrderDate(new Date().toISOString().slice(0, 16));
    setDeliveryDate("");
  };

  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.productId === product.id);
      if (existing) {
        return prev.map((item) =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { productId: product.id, product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.productId === productId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.productId !== productId));
  };

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      const price = parseFloat(item.product.unitPrice || "0");
      return total + price * item.quantity;
    }, 0);
  };

  const handleSubmit = () => {
    if (!selectedClientId || cart.length === 0) return;
    const payload: any = {
      clientId: selectedClientId,
      items: cart.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
      })),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(deliveryDate ? { deliveryDate } : {}),
    };
    if (showStatusAndDate) {
      payload.status = selectedStatus;
      payload.orderDate = orderDate;
    }
    createOrderMutation.mutate(payload);
  };

  const activeProducts = products?.filter((p) => p.isActive) || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lägg till order manuellt</DialogTitle>
          <DialogDescription>Skapa en ny order för en kund</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label>Välj kund</Label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="mt-1" data-testid="select-client">
                <SelectValue placeholder="Välj en kund" />
              </SelectTrigger>
              <SelectContent>
                {clients?.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showStatusAndDate && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="mt-1" data-testid="select-order-status">
                    <SelectValue placeholder="Välj status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Orderdatum</Label>
                <Input
                  type="datetime-local"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  className="mt-1"
                  data-testid="input-order-date"
                />
              </div>
            </div>
          )}

          <div>
            <Label className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              Framtida leveransdatum <span className="text-muted-foreground font-normal">(valfritt)</span>
            </Label>
            <Input
              type="date"
              value={deliveryDate}
              min={minDeliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              className="mt-1"
              data-testid="input-delivery-date"
            />
            {deliveryDate && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                Planerad leverans: {new Date(deliveryDate + "T12:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" })}
              </p>
            )}
          </div>

          <div>
            <Label>Lägg till produkter</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {activeProducts.map((product) => (
                <Button
                  key={product.id}
                  variant="outline"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => addToCart(product)}
                  data-testid={`button-add-product-${product.id}`}
                >
                  <Plus className="h-4 w-4 mr-2 shrink-0" />
                  <span className="truncate">{product.name}</span>
                </Button>
              ))}
            </div>
          </div>

          {cart.length > 0 && (
            <div>
              <Label>Varukorg</Label>
              <div className="mt-2 space-y-2">
                {cart.map((item) => (
                  <div
                    key={item.productId}
                    className="flex items-center justify-between gap-2 p-3 border rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.product.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.product.unitPrice} kr/{getUnitLabel(item.product.sellUnit)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item.productId, -1)}
                        data-testid={`button-decrease-${item.productId}`}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setCart((prev) =>
                            prev.map((i) =>
                              i.productId === item.productId ? { ...i, quantity: val } : i
                            ).filter((i) => i.quantity > 0)
                          );
                        }}
                        className="w-16 text-center"
                        min={1}
                      />
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item.productId, 1)}
                        data-testid={`button-increase-${item.productId}`}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeFromCart(item.productId)}
                        data-testid={`button-remove-${item.productId}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <span>Totalt</span>
                  <span>{calculateTotal().toFixed(2)} kr</span>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label>Anteckningar <span className="text-muted-foreground font-normal">(valfritt)</span></Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Eventuella noteringar för ordern..."
              className="mt-1 resize-none"
              rows={2}
              data-testid="input-order-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { resetForm(); onOpenChange(false); }}>
            Avbryt
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedClientId || cart.length === 0 || createOrderMutation.isPending}
            data-testid="button-create-order"
          >
            {createOrderMutation.isPending ? "Skapar..." : "Skapa order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
